import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AnimatePresence } from 'framer-motion';
import { startQuizSession, validateQuizSession } from '../../services/quizSessionService';
import { getQuiz, submitQuizAnswers } from '../../services/quizService';
import QuizCompletionModal from '../../components/modals/QuizCompletionModal';
import BackLink from '../../components/ui/BackLink';
import QuestionCard from './components/QuestionCard';
import { toast } from 'sonner';
import { formatTimeLeft, isSessionExpired, parseDuration } from './utils/utils';
import { clearSession, getStoredSession, storeSession } from './utils/quizSessionStorage';
import QuizQuestionTracker from './components/QuizQuestionTracker';

const StartQuizPage = () => {
  const { quiz_id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState(0);
  const [sessionInfo, setSessionInfo] = useState(getStoredSession(quiz_id));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef();

  let _setSessionInfo = (quiz_id, sessionId, startedAt) => {    
    setSessionInfo({ sessionId, startedAt });
    storeSession(quiz_id, sessionId, startedAt);
  };

  useEffect(() => {
    const initializeQuiz = async () => {
      try {
        setLoading(true);
        const quizData = await getQuiz(quiz_id);
        setQuiz(quizData);
        
        // Check if quiz has questions before starting/validating session
        if (quizData.questions.length === 0) {
          return;
        }

        const existingSession = getStoredSession(quiz_id);
        console.log('existingSession:', existingSession);

        // If no existing session or session is expired, start a new session
        if (!existingSession || isSessionExpired(existingSession.existingSession, quizData.expected_duration)) {
          console.log('starting new quiz session')
          const sessionData = await startQuizSession(quiz_id);
          _setSessionInfo(quiz_id, sessionData.id, sessionData.started_at);
        } else {
          console.log('using existing quiz session')
          try {
            const sessionData = await validateQuizSession(existingSession.sessionId);
            _setSessionInfo(quiz_id, sessionData.data.id, sessionData.data.started_at);
          } catch {
            const sessionData = await startQuizSession(quiz_id);
            _setSessionInfo(quiz_id, sessionData.id, sessionData.started_at);
          }
        }

      } catch (error) {
        console.error('Error initializing quiz:', error);
        toast.error('Failed to start quiz');
        // navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    initializeQuiz();
  }, [quiz_id]);

  // Timer effect
  useEffect(() => {
    if (!quiz?.expected_duration || !sessionInfo?.startedAt) return;

    const durationSeconds = parseDuration(quiz.expected_duration);
    const startedAt = new Date(sessionInfo.startedAt).getTime();
    const endAt = startedAt + durationSeconds * 1000;

    function updateTimer() {
      const now = Date.now();
      const secondsLeft = Math.max(0, Math.floor((endAt - now) / 1000));
      setTimeLeft(secondsLeft);

      if (secondsLeft <= 0) {
        clearInterval(timerRef.current);
        if (quiz.quiz_type === 'assessment') {
          handleSubmitQuiz();
        }
      }
    }

    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);

    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [quiz?.expected_duration, sessionInfo?.startedAt]);

  const handleSubmitQuiz = async () => {
    if (!sessionInfo?.sessionId) return;    
    
    try {
      setIsSubmitting(true);
      await submitQuizAnswers(sessionInfo?.sessionId, Object.values(answers));
      clearSession(quiz_id);
      setShowCompletionModal(true); // Show completion modal instead of navigating
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAnyQuestionUnanswered = () => {
    return quiz.questions.some((question) => {
      const answer = answers[question.id];
      
      if (question.question_type === 'objective') {
        return !answer?.selected_option;
      } else if  (question.question_type === 'theory'){
        return !answer?.theory_answer?.trim();
      }
    });
  };

  const handleNext = () => {
    setDirection(1);
    
    if (currentQuestionIndex === quiz.questions.length - 1) {
      if(isAnyQuestionUnanswered()){
        toast.error('Please answer all questions before submitting.');
        return;
      }
      handleSubmitQuiz();
      
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setDirection(-1);
      setCurrentQuestionIndex(prev => prev - 1);
      const prevQuestion = quiz.questions[currentQuestionIndex -1];
    }
  };

  const handleMultipleSelect = (optionId) => {
    setAnswers(prev => {
      const prevSelected = prev[currentQuestion.id]?.selected_options || [];
      const alreadySelected = prevSelected.includes(optionId);
      const newSelected = alreadySelected
        ? prevSelected.filter(id => id !== optionId)
        : [...prevSelected, optionId];
      return {
        ...prev,
        [currentQuestion.id]: {
          question: currentQuestion.id,
          type: 'multiple_select',
          selected_options: newSelected,
        }
      };
    });
  };

  if (loading || !quiz) {
    return <div className="text-center py-4">Loading...</div>;
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex justify-between items-center">
        <BackLink />
        <div className="flex items-center gap-4">
          <div className="badge badge-lg badge-primary badge-outline p-3">{quiz?.topic_detail?.title || quiz?.course_title}</div>
          {timeLeft !== null && (
            <div className={`badge badge-lg ${timeLeft < 60 ? 'badge-error' : 'badge-outline'} p-3`}>
              Time Left: {formatTimeLeft(timeLeft)}
            </div>
          )}
        </div>
      </div>

      <QuizQuestionTracker
        questions={quiz.questions}
        answers={answers}
        currentIndex={currentQuestionIndex}
        onJump={(idx) => {
          setCurrentQuestionIndex(idx);
          setDirection(idx > currentQuestionIndex ? 1 : -1);
        }}
      />

      {/* Quiz Progress */}
      <div className="card bg-base-200 shadow-md mb-8">
        <div className="card-body p-4">
          <div className="flex justify-between items-center">
            <h2 className="card-title text-lg">Quiz Progress</h2>
            <div className="text-sm">Question {currentQuestionIndex + 1} of {quiz.questions.length}</div>
          </div>
          <progress className="progress progress-primary w-full" value={currentQuestionIndex + 1} max={quiz.questions.length}></progress>
        </div>
      </div>

      {quiz.questions.length === 0 ? (
        <div className="text-center py-8">
           <div className="text-5xl mb-4">😢</div>
          <h1 className="text-xl text-gray-600">No questions have been added to this quiz yet </h1>
          <button 
            className="btn btn-outline mt-4"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      ) : (
        <>
          <div className="relative mb-4 overflow-x-hidden">
            <AnimatePresence initial={false} custom={direction}>
              <QuestionCard
                key={currentQuestion.id}
                quizType={quiz.quiz_type}
                question={currentQuestion}
                answer={answers[currentQuestion.id] || {}}
                questionNumber={currentQuestionIndex + 1}
                onOptionSelect={(optionId) => {
                  setAnswers(prev => ({
                    ...prev,
                    [currentQuestion.id]: {
                      question: currentQuestion.id,
                      type: 'objective',
                      selected_option: optionId,
                    }
                  }));
                }}
                theoryAnswer={answers[currentQuestion.id]?.theory_answer || ''}
                onTheoryChange={(value) => setAnswers(prev => ({
                  ...prev,
                  [currentQuestion.id]: {
                    question: currentQuestion.id,
                    type: 'theory',
                    theory_answer: value
                  }
                }))}
                onMultipleSelect={handleMultipleSelect}
                onMatchingChange={(pairId, selectedRight) => {
                  setAnswers(prev => {
                    const prevMatches = prev[currentQuestion.id]?.matching_answers || [];
                    const updatedMatches = prevMatches.find(match => match.matching_pair == pairId) ?
                      prevMatches.map((match)=> (match.matching_pair == pairId ? {...match, selected_right: selectedRight} : match )) :
                      [...prevMatches, { matching_pair: pairId, selected_right: selectedRight }]

                    return {
                      ...prev,
                      [currentQuestion.id]: {
                        question: currentQuestion.id,
                        type: 'matching',
                        matching_answers: updatedMatches
                      }
                    }
                  })
                }}
                direction={direction}
              />
            </AnimatePresence>
          </div>

          <div className="flex justify-between mb-8">
            <button
              className="btn btn-outline"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0 || isSubmitting}
            >
              Previous
            </button>
            <button
              className="btn btn-primary"
              onClick={handleNext}
              disabled={isSubmitting}
            >
              {currentQuestionIndex === quiz.questions.length - 1 ? (
                isSubmitting ? 'Submitting...' : 'Submit'
              ) : 'Next'}
            </button>
          </div>
        </>
      )}

      <QuizCompletionModal 
        isOpen={showCompletionModal} 
        sessionId={sessionInfo?.sessionId}
      />
    </div>
  );
};

export default StartQuizPage;
